import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog5 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

const Blog5 = () => {
  const navigate = useNavigate();
      const handleBackClick = () => {
         window.scrollTo(0, 0);
         navigate("/blog")
      };

  return (
      <>
     <Fullpage>
     <PageBackground>
      <BlogContainer>
      <BlogWrapper>
        <HeaderSection>
          <BackButtonContainer>
            <BackButton onClick={handleBackClick}>
              Back to Blogs
            </BackButton>
          </BackButtonContainer>
          <BlogHeader
            title="The AI Campus Revolution: How Smart Technology is Reshaping Higher Education"
            date="August 3, 2025"
            author="posted by campus insights"
          />
        </HeaderSection>

        <HeroImage
          src={blog5}
          alt="AI Campus Revolution hero image"
        />

        <BlogContent />
      </BlogWrapper>
      </BlogContainer>
      <OtherBlogs currentBlogId={5} />
      </PageBackground>
      </Fullpage>
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <Date>{date}</Date>
            <Author>{author}</Author>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          Artificial intelligence is no longer a distant vision—it's the reality reshaping higher education today. In 2025, forward-thinking institutions are already seeing the benefits of AI: improved student retention, streamlined operations, and stronger engagement across campus.
        </IntroParagraph>
        <IntroParagraph>
          This shift isn't gradual; it's transformational. Colleges and universities are moving from disconnected systems and reactive services to intelligent, adaptive environments that prioritize the student experience and institutional resilience.
        </IntroParagraph>
      </IntroSection>

      <BlogSection title="The Pressure is Real: Why Higher Ed Must Embrace AI Now">
        <SubtitleText>
          Higher education is at a turning point. Traditional approaches are struggling to meet new demands:
        </SubtitleText>
        <ContentParagraph>
          Budget constraints are forcing institutions to do more with less.
        </ContentParagraph>
        <ContentParagraph>
          Digital-native students expect fast, personalized, always-available services.
        </ContentParagraph>
        <ContentParagraph>
          Administrative complexity continues to grow, stretching already-limited staff.
        </ContentParagraph>
        <ContentParagraph>
          Competition is intensifying, with students gravitating toward tech-forward institutions.
        </ContentParagraph>
        <ContentParagraph>
          To thrive in this environment, colleges need more than incremental fixes. They need AI—built into the very fabric of campus operations.
        </ContentParagraph>
      </BlogSection>

      <BlogSection title="The Three Forces Powering the AI Campus">
        <BlogSection title="1. 🤖 Cognitive Automation That Works" level={3}>
          <ContentParagraph>
            AI goes far beyond basic chatbots or task reminders. It can understand intent, context, and urgency—and act accordingly.
          </ContentParagraph>
          <ContentParagraph>
            When a student says, "I'm thinking of dropping a class," AI doesn't just respond with policy—it recognizes a retention risk and suggests action.
          </ContentParagraph>
          <ContentParagraph>
            End-to-end automation reduces paperwork and friction in processes like admissions, financial aid, course registration, and academic advising.
          </ContentParagraph>
          <ContentParagraph>
            Instead of waiting for issues to arise, AI can proactively nudge students before deadlines, suggest tutoring, or surface wellness resources.
          </ContentParagraph>
          <ContentParagraph>
            This is automation that supports real outcomes—not just faster clicks.
          </ContentParagraph>
        </BlogSection>

        <BlogSection title="2. 📊 Predictive Intelligence That Prevents Drop-Off" level={3}>
          <ContentParagraph>
            AI isn't just smart—it's insightful. Institutions are moving from reacting to dropouts to predicting them before they happen.
          </ContentParagraph>
          <ContentParagraph>
            Early warning systems use academic, behavioral, and emotional indicators to flag at-risk students.
          </ContentParagraph>
          <ContentParagraph>
            AI identifies success signals too, helping replicate what works across different student groups.
          </ContentParagraph>
          <ContentParagraph>
            Forecasting tools allow administrators to see beyond the current semester—predicting enrollment patterns, staff needs, and financial aid loads well in advance.
          </ContentParagraph>
          <ContentParagraph>
            This shift to proactive support is revolutionizing how colleges retain students and plan ahead.
          </ContentParagraph>
        </BlogSection>

        <BlogSection title="3. 🎯 Instant Decision Intelligence for Leaders" level={3}>
          <ContentParagraph>
            Every conversation, form submission, and engagement becomes a data point. AI turns this into a live feedback loop for smarter leadership.
          </ContentParagraph>
          <ContentParagraph>
            Monitor campus sentiment in real time to spot emerging issues before they escalate.
          </ContentParagraph>
          <ContentParagraph>
            Track which services are underperforming and where bottlenecks are slowing progress.
          </ContentParagraph>
          <ContentParagraph>
            Replace gut-feel decision-making with actionable intelligence that guides long-term strategy.
          </ContentParagraph>
          <ContentParagraph>
            Data is no longer buried in spreadsheets—it's available at the moment decisions are made.
          </ContentParagraph>
        </BlogSection>
      </BlogSection>

      <BlogSection title="The Rise of Unified Campus Intelligence">
        <ContentImage
          src="https://api.builder.io/api/v1/image/assets/TEMP/97ee1841dfad99570e0ad36f04ce419b0781dcec?width=1526"
          alt="Unified Campus Intelligence visualization"
        />
        <ContentParagraph>
          An AI-powered campus isn't a patchwork of tools—it's an integrated ecosystem where systems talk to each other, teams collaborate, and students experience seamless support.
        </ContentParagraph>
        <SubtitleText>Imagine:</SubtitleText>
        <ContentParagraph>
          A holistic student journey view, from first inquiry to graduation, mapped in real-time.
        </ContentParagraph>
        <ContentParagraph>
          Cross-functional teams—from admissions to academic advising—working from the same insights.
        </ContentParagraph>
        <ContentParagraph>
          AI that learns and adapts over time, refining support based on what actually helps your students succeed.
        </ContentParagraph>
        <ContentParagraph>
          This is what unification looks like: not just better tech, but smarter campuses.
        </ContentParagraph>
      </BlogSection>

      <BlogSection title="What's Shaping the 2025 AI-Driven Campus">
        <SubtitleText>Several trends are defining this era of transformation:</SubtitleText>
        <ContentParagraph>
          Humanized generative AI: Conversations that feel natural, helpful, and empathetic.
        </ContentParagraph>
        <ContentParagraph>
          Mobile-first design: Student support must work anywhere, anytime, on any device.
        </ContentParagraph>
        <ContentParagraph>
          Privacy by design: Transparency, data control, and ethical usage are non-negotiable.
        </ContentParagraph>
        <ContentParagraph>
          Ecosystem integration: AI doesn't replace your systems—it enhances them.
        </ContentParagraph>
        <ContentParagraph>
          The AI-powered campus doesn't disrupt what works—it supercharges it.
        </ContentParagraph>
      </BlogSection>

      <BlogSection title="Why Timing Matters">
        <SubtitleText>
          AI in higher education isn't futuristic—it's now. Institutions that move quickly have the advantage:
        </SubtitleText>
        <ContentParagraph>Faster adoption curves = quicker ROI</ContentParagraph>
        <ContentParagraph>First-mover perception = stronger appeal to prospective students</ContentParagraph>
        <ContentParagraph>Efficient operations = more time and resources for strategic growth</ContentParagraph>
        <ContentParagraph>Stronger student outcomes = measurable impact on graduation and success</ContentParagraph>
      </BlogSection>

      <BlogSection title="Leading the Future—With the Right Partner">
        <ContentParagraph>
          If your institution is ready to unlock this AI-powered future, Campus Insights is built to help you lead.
        </ContentParagraph>
        <ContentParagraph>
          We don't offer generic chatbots—we deliver higher education-specific AI that understands the student lifecycle, integrates seamlessly into your existing systems, and empowers staff with real-time, actionable insights.
        </ContentParagraph>
        <ContentParagraph>
          From personalized engagement to predictive analytics and institutional dashboards, Campus Insights helps you serve students better, plan smarter, and operate more efficiently.
        </ContentParagraph>
      </BlogSection>

      <ClosingSection>
        <ClosingText>
          Ready to lead the AI campus revolution? Let's build your intelligent campus—together.
        </ClosingText>
        <CallToAction>👉 [Schedule a Demo]</CallToAction>
        <FinalText>
          The AI revolution in higher education is happening now. The only question is: will you lead it, or will you be left behind?
        </FinalText>
      </ClosingSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children, level = 2 }) => {
  const HeadingComponent = level === 2 ? SectionTitle : SubsectionTitle;

  return (
    <SectionContainer>
      {title && <HeadingComponent>{title}</HeadingComponent>}
      <ContentContainer1>{children}</ContentContainer1>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 28px;
  }
  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const SubsectionTitle = styled.h3`
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const ContentContainer1 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;


const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const IntroParagraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const SubtitleText = styled.p`
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const ContentParagraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const ContentImage = styled.img`
  width: 763px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 991px) {
    width: 100%;
    max-width: 763px;
  }
  @media (max-width: 640px) {
    height: 250px;
  }
`;

const ClosingSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ClosingText = styled.p`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
`;

const CallToAction = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const FinalText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;


const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const Title = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 36px;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 130%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const Date = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const Author = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const BlogContainer = styled.article`
  display: flex;
  width: 100%;
  max-width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    padding: 40px;
    gap: 48px;
  }
  @media (max-width: 640px) {
    padding: 24px;
    gap: 32px;
  }
`;

const BlogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  width: 100%;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
`;

const BackButtonContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 640px) {
    height: 250px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

export default Blog5;
